import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useItems } from '../contexts/ItemsContext';
import { useUser } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import './Branding.css';

const ItemCard = ({ item, onOpen, onToggleFavorite, isFavorite }) => {

  const front = (item.files || []).find(f => f.for_show && f.type === 'image') || (item.files || []).find(f => f.type === 'image');
  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    onToggleFavorite(item);
  };

  return (
    <button className="program-card" onClick={() => onOpen(item)}>
      <div className="program-card-border">
        <div className="program-card-media">
          {front ? <img src={front.url} alt={item.name} /> : <div className="program-card-placeholder">אין תמונה</div>}
          <button
            className={`favorite-btn${isFavorite ? ' favorite-active' : ''}`}
            onClick={handleFavoriteClick}
            aria-label={isFavorite ? 'הסר מהאהובים' : 'הוסף לאהובים'}
            type="button"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill={isFavorite ? "#e53935" : "none"}
              stroke="#e53935"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ display: 'block' }}
            >
              <path d="M12 21C12 21 4 13.36 4 8.5C4 5.42 6.42 3 9.5 3C11.24 3 12.91 3.81 14 5.08C15.09 3.81 16.76 3 18.5 3C21.58 3 24 5.42 24 8.5C24 13.36 16 21 16 21H12Z"/>
            </svg>
          </button>
        </div>
        <div className="program-card-footer">
          <div className="program-card-name">{item.name}</div>
          {item.features && item.features.length > 0 && (
            <div className="program-card-features">
              {item.features.map(f => <span key={f.id} className="feature-chip">{f.name}</span>)}
            </div>
          )}
          <div className="program-card-meta"> {item.price} ₪</div>
          <div className="program-card-meta"> {item.much_for}</div>
          {item.video && <div className="program-card-video">🎬</div>}
        </div>
      </div>
    </button>
  );
};

const styleOptions = ['מכובד', 'צעיר'];
const timingOptions = ['חנוכה', 'פורים', 'ט"ו בשבט', 'יומולדת'];

const Branding = () => {
  const { itemsByGenre, loadItems, loadingByGenre, errorByGenre } = useItems();
  const { user, addFavorite, removeFavorite } = useUser();
  const navigate = useNavigate();
  const genre = 'מיתוג';

  useEffect(() => {
    loadItems(genre);
  }, []);

  const loading = loadingByGenre[genre];
  const error = errorByGenre[genre];
  const items = itemsByGenre[genre] || [];

  // פילטרים
  const [selected, setSelected] = useState({ types: new Set(), timings: new Set() });
  const [openFilter, setOpenFilter] = useState({ types: false, timings: false });
  const typesRef = useRef(null);
  const timingsRef = useRef(null);

  // סגירת חלוניות בלחיצה בחוץ
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        typesRef.current && !typesRef.current.contains(e.target) &&
        timingsRef.current && !timingsRef.current.contains(e.target)
      ) {
        setOpenFilter({ types: false, timings: false });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleValue = (setKey, value) => {
    setSelected(prev => {
      const copy = { ...prev };
      const set = new Set(prev[setKey]);
      if (set.has(value)) set.delete(value); else set.add(value);
      copy[setKey] = set;
      return copy;
    });
  };

  const toggleFilterPanel = (filterName) => {
    setOpenFilter(prev => ({
      types: false,
      timings: false,
      [filterName]: !prev[filterName]
    }));
  };

  const filteredItems = useMemo(() => {
    return items.filter(it => {
      
      if (selected.timings.size > 0) {
        const itTimes = (it.timings || []).map(t => (typeof t === 'object' ? t.name || t.id : String(t)));
        const hasAny = Array.from(selected.timings).some(s => itTimes.includes(s));
        if (!hasAny) return false;
      }

      if (selected.types.size > 0) {
        const itTypes = (it.types || []).map(t => (typeof t === 'object' ? t.name || t.id : String(t)));
        const hasAny = Array.from(selected.types).some(s => itTypes.includes(s));
        if (!hasAny) return false;
      }
      return true;
    });
  }, [items, selected]);

  const openItem = (item) => {
    navigate(`/item/${item.id}`);
  };

  const isItemFavorite = (item) => {
    if (!user?.likes) return false;
    return user.likes.some(like => like.id === item.id);
  };

  const toggleFavorite = async (item) => {
    if (!user) return;
    if (isItemFavorite(item)) {
      await removeFavorite(item.id);
    } else {
      await addFavorite(item);
    }
  };

  return (
    <div className="programs-container">
      <div className="programs-frame">
        <div className="programs-filters">
          <div className="filters-row">
            <button type="button" className="filter-clear" onClick={() => setSelected({ types: new Set(), timings: new Set() })}>
              נקה מסננים
            </button>
            {/* Filter: types */}
            <div className="filter" ref={typesRef}>
              <button type="button" className="filter-btn" onClick={() => toggleFilterPanel('types')}>
                <span style={{ color: 'hotpink' }}>v</span>
                {` סגנון | \u200E${selected.types.size > 0 ? selected.types.size : '0'}`}
              </button>
              {openFilter.types && (
                <div className="filter-panel">
                  {styleOptions.map(opt => (
                    <label key={opt} className="filter-option">
                      <input type="checkbox" checked={selected.types.has(opt)} onChange={() => toggleValue('types', opt)} />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
            </div>
            {/* Filter: timings */}
            <div className="filter" ref={timingsRef}>
              <button type="button" className="filter-btn" onClick={() => toggleFilterPanel('timings')}>
                <span style={{ color: 'hotpink' }}>v</span>
                {` עיתוי | \u200E${selected.timings.size > 0 ? selected.timings.size : '0'}`}
              </button>
              {openFilter.timings && (
                <div className="filter-panel">
                  {timingOptions.map(opt => (
                    <label key={opt} className="filter-option">
                      <input type="checkbox" checked={selected.timings.has(opt)} onChange={() => toggleValue('timings', opt)} />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="line"></div>
          <div className="filters-actions">
            <div className="filters-count">
              מציג {filteredItems.length} מתוך {items.length}
              <span> | </span>
              מתלבטת מה מתאים לך?<a href="/contactus" className="contactus-inline-link"> צרי איתנו קשר</a>
            </div>
          </div>
        </div>
        {loading && <div className="programs-loading">...חכי שניה, זה עולה</div>}
        {error && !items && items.length <= 0 && <div className="programs-error"> אופסססס, פשלה! איזה בושות! </div>}
        <div className="programs-grid">
          {filteredItems.map(it => (
            <ItemCard
              key={it.id}
              item={it}
              onOpen={openItem}
              onToggleFavorite={toggleFavorite}
              isFavorite={isItemFavorite(it)}
            />
          ))}
        </div>
        {!loading && !error && items.length === 0 && <div className="programs-empty">אין כלום... חבל</div>}
      </div>
    </div>
  );
};

export default Branding;
